define(
    'NMSDesignSystem.CookieConsent',
    [
        'Backbone',
        'Configuration',
        'underscore',
        'nms_cookie_consent.tpl',
        'Tracker',
        'GoogleTagManager',
        'GoogleTagManager.Model',
        'NMSDesignSystem.MainAnalytics'
    ],
    function NMSDesignSystemCookieConsent(
        Backbone,
        Configuration,
        _,
        nmsCookieConsentTpl,
        Tracker,
        GoogleTagManager,
        GoogleTagManagerModel,
        MainAnalytics
    ) {
        'use strict';

        Configuration = Configuration.Configuration || Configuration;

        var consentCookie = 'nms_cookie_consent';
        var nativeDismissalCookie = 'isCookieWarningClosed';
        var consentVersion = 1;
        var consentDurationDays = 180;
        var consent;
        var analyticsWasLoaded = false;
        var activeView;
        var documentHandlerInstalled = false;
        var lifecycleBound = false;
        var originalTracking;
        var mainAnalytics;

        Configuration.tracking = Configuration.tracking || {};
        Configuration.recentlyViewedItems = Configuration.recentlyViewedItems || {};
        Configuration.siteSettings = Configuration.siteSettings || {};
        Configuration.cookieWarningBanner = Configuration.cookieWarningBanner || {};
        originalTracking = {
            tagManager: Configuration.tracking.googleTagManager || {},
            universalAnalytics: Configuration.tracking.googleUniversalAnalytics || {},
            recentlyViewed: Configuration.recentlyViewedItems.useCookie
        };

        mainAnalytics = MainAnalytics.install(originalTracking.tagManager);

        function expireCookie(name) {
            var hostParts = window.location.hostname.split('.');
            var rootDomain = hostParts.length > 2 ? '.' + hostParts.slice(-2).join('.') : '';
            var paths = ['/', '/checkout'];
            var domains = rootDomain ? ['', rootDomain] : [''];

            _.each(paths, function expireAtPath(path) {
                _.each(domains, function expireAtDomain(domain) {
                    document.cookie = encodeURIComponent(name) +
                        '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=' + path +
                        (domain ? '; domain=' + domain : '') + '; SameSite=Lax';
                });
            });
        }

        function readCookie(name) {
            var encodedName = encodeURIComponent(name) + '=';
            var cookies = document.cookie ? document.cookie.split(';') : [];
            var cookie;
            var index;

            for (index = 0; index < cookies.length; index += 1) {
                cookie = cookies[index].replace(/^\s+/, '');
                if (cookie.indexOf(encodedName) === 0) {
                    try {
                        return decodeURIComponent(cookie.slice(encodedName.length));
                    } catch (error) {
                        return null;
                    }
                }
            }
            return '';
        }

        function isValidConsent(value) {
            return !!value &&
                value.version === consentVersion &&
                !!value.preferences &&
                typeof value.preferences.analytics === 'boolean' &&
                typeof value.preferences.personalization === 'boolean';
        }

        function readConsent() {
            var raw = readCookie(consentCookie);
            var parsed;

            if (!raw) {
                if (raw === null) {
                    expireCookie(consentCookie);
                }
                return null;
            }

            try {
                parsed = JSON.parse(raw);
            } catch (error) {
                expireCookie(consentCookie);
                return null;
            }

            if (!isValidConsent(parsed)) {
                expireCookie(consentCookie);
                return null;
            }

            return parsed;
        }

        function writeConsent(value) {
            var expires = new Date(Date.now() + consentDurationDays * 86400000).toUTCString();
            var saved;

            try {
                document.cookie = encodeURIComponent(consentCookie) + '=' +
                    encodeURIComponent(JSON.stringify(value)) + '; expires=' + expires +
                    '; path=/; SameSite=Lax' + (window.location.protocol === 'https:' ? '; Secure' : '');
                saved = readConsent();
            } catch (error) {
                return false;
            }

            return !!saved &&
                saved.preferences.analytics === value.preferences.analytics &&
                saved.preferences.personalization === value.preferences.personalization;
        }

        function writeNativeDismissalCookie() {
            var expires = new Date(Date.now() + consentDurationDays * 86400000).toUTCString();

            document.cookie = encodeURIComponent(nativeDismissalCookie) +
                '=T; expires=' + expires + '; path=/; SameSite=Lax' +
                (window.location.protocol === 'https:' ? '; Secure' : '');
        }

        function removeCookiesMatching(test) {
            _.each(document.cookie ? document.cookie.split(';') : [], function removeCookie(cookie) {
                var name = cookie.split('=')[0].replace(/^\s+/, '');

                if (test(name)) {
                    expireCookie(name);
                }
            });
        }

        function revokeOptionalCookies(preferences) {
            if (!preferences.analytics) {
                removeCookiesMatching(function isAnalyticsCookie(name) {
                    return /^_(ga|gid|gat)/i.test(name) || name === 'NLVisitorId';
                });
            }
            if (!preferences.personalization) {
                expireCookie('recentlyViewedIds');
            }
        }

        function applyStartupConsent(value) {
            var preferences = value && value.preferences || {};

            MainAnalytics.setConsent(preferences.analytics === true);
            if (mainAnalytics) {
                // Main's GA4 container owns ecommerce tracking. Do not also
                // register UA: its Google-tag listener forwards duplicate actions.
                Configuration.tracking.googleUniversalAnalytics = {propertyID: ''};
            }
            if (preferences.analytics !== true) {
                Configuration.tracking.googleTagManager = {
                    id: '',
                    dataLayerName: originalTracking.tagManager.dataLayerName || 'dataLayer'
                };
                Configuration.tracking.googleUniversalAnalytics = {
                    propertyID: '',
                    domainName: '',
                    domainNameSecure: ''
                };
                if (window.NLRUM) {
                    window.NLRUM.autorun = false;
                    window.NLRUM.bSendBeacon = 0;
                }
            }
            if (preferences.personalization !== true) {
                Configuration.recentlyViewedItems.useCookie = false;
            }

            Configuration.siteSettings.showcookieconsentbanner = 'F';
            Configuration.cookieWarningBanner.message = '';
        }

        function addScript(id, source) {
            var script;

            if (document.getElementById(id) ||
                document.querySelector('script[src="' + source.replace(/"/g, '\\"') + '"]')) {
                return;
            }
            script = document.createElement('script');
            script.id = id;
            script.async = true;
            script.src = source;
            (document.head || document.documentElement).appendChild(script);
        }

        // Native GTM skips registration when consent clears its ID at startup.
        // Register on explicit acceptance without remounting: native mount resets
        // the data layer and installs another script/lifecycle callback.
        function registerAcceptedTagManager() {
            var trackers;
            if (!originalTracking.tagManager.id) {
                return;
            }
            trackers = Tracker.getInstance().trackers;
            if (_.indexOf(trackers, GoogleTagManager) !== -1) {
                return;
            }
            GoogleTagManager.configuration = originalTracking.tagManager;
            GoogleTagManager.configuration.dataLayerName =
                GoogleTagManager.configuration.dataLayerName || 'dataLayer';
            GoogleTagManager.model = GoogleTagManager.model || new GoogleTagManagerModel();
            window[GoogleTagManager.configuration.dataLayerName] =
                window[GoogleTagManager.configuration.dataLayerName] || [];
            trackers.push(GoogleTagManager);
        }

        function startAnalytics(registerTracker) {
            var tagManager = originalTracking.tagManager;
            var universalAnalytics = originalTracking.universalAnalytics;
            var layerName;

            if (mainAnalytics) {
                MainAnalytics.setConsent(true);
                if (!registerTracker) {
                    // Remembered consent uses native GTM's normal mount/loader.
                    // Explicit acceptance below enables the tracker without reload.
                    analyticsWasLoaded = true;
                    return true;
                }
            }
            if (registerTracker) {
                registerAcceptedTagManager();
            }
            if (analyticsWasLoaded) {
                return true;
            }

            try {
                Configuration.tracking.googleTagManager = tagManager;
                Configuration.tracking.googleUniversalAnalytics = mainAnalytics ? {propertyID: ''} : universalAnalytics;

                if (tagManager.id) {
                    layerName = tagManager.dataLayerName || 'dataLayer';
                    window[layerName] = window[layerName] || [];
                    window[layerName].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
                    addScript(
                        'nms-google-tag-manager',
                        'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(tagManager.id) +
                            '&l=' + encodeURIComponent(layerName)
                    );
                }
                if (!mainAnalytics && universalAnalytics.propertyID) {
                    window.GoogleAnalyticsObject = 'ga';
                    window.ga = window.ga || function googleAnalyticsQueue() {
                        (window.ga.q = window.ga.q || []).push(arguments);
                    };
                    window.ga.l = Number(new Date());
                    window.ga('create', universalAnalytics.propertyID, {
                        cookieDomain: universalAnalytics.domainName || 'auto',
                        allowLinker: true
                    });
                    window.ga('send', 'pageview');
                    addScript('nms-google-universal-analytics', 'https://www.google-analytics.com/analytics.js');
                }
                analyticsWasLoaded = true;
                return true;
            } catch (error) {
                analyticsWasLoaded = false;
                return false;
            }
        }

        function removeNativeBanners() {
            _.each(
                document.querySelectorAll('.cookie-warning-banner-view, .header-message.nms-cookie-banner-placeholder'),
                function removeNativeBanner(node) {
                    if (node.parentNode) {
                        node.parentNode.removeChild(node);
                    }
                }
            );
        }

        function restoreFocus(view) {
            var target = view && view.previouslyFocusedElement;

            if (target && document.documentElement.contains(target) && typeof target.focus === 'function') {
                target.focus();
            }
        }

        var CookieConsentView = Backbone.View.extend({
            template: nmsCookieConsentTpl,
            tagName: 'section',
            id: 'nms-cookie-consent',
            className: 'nms-cookie-consent',

            attributes: {
                role: 'dialog',
                'aria-modal': 'false',
                'aria-labelledby': 'nms-cookie-consent-title'
            },

            events: {
                'click [data-action="accept-necessary"]': 'acceptNecessary',
                'click [data-action="accept-all"]': 'acceptAll',
                'click [data-action="manage"]': 'showPreferences',
                'click [data-action="cancel-manage"]': 'hidePreferences',
                'click [data-action="save"]': 'savePreferences'
            },

            initialize: function initializeCookieConsentView() {
                this.manageMode = false;
                this.storageError = false;
                this.previouslyFocusedElement = document.activeElement;
            },

            render: function renderCookieConsentView() {
                Backbone.View.prototype.render.apply(this, arguments);
                this.$el
                    .attr('data-nms-cookie-ready', 'true')
                    .toggleClass('nms-cookie-consent--manage', this.manageMode);
                return this;
            },

            showPreferences: function showPreferences(event) {
                event.preventDefault();
                this.manageMode = true;
                this.storageError = false;
                this.render();
                this.$('[name="nms-analytics"]').focus();
            },

            hidePreferences: function hidePreferences(event) {
                event.preventDefault();
                this.manageMode = false;
                this.storageError = false;
                this.render();
                this.$('[data-action="manage"]').focus();
            },

            acceptNecessary: function acceptNecessary(event) {
                event.preventDefault();
                this.saveConsent({analytics: false, personalization: false});
            },

            acceptAll: function acceptAll(event) {
                event.preventDefault();
                this.saveConsent({analytics: true, personalization: true});
            },

            savePreferences: function savePreferences(event) {
                event.preventDefault();
                this.saveConsent({
                    analytics: this.$('[name="nms-analytics"]').prop('checked'),
                    personalization: this.$('[name="nms-personalization"]').prop('checked')
                });
            },

            saveConsent: function saveConsent(preferences) {
                var previousPreferences = consent && consent.preferences || {};
                var nextConsent = {
                    version: consentVersion,
                    preferences: {
                        analytics: preferences.analytics === true,
                        personalization: preferences.personalization === true
                    },
                    updatedAt: new Date().toISOString()
                };

                if (!writeConsent(nextConsent)) {
                    this.storageError = true;
                    this.render();
                    this.$('.nms-cookie-consent__error').focus();
                    return;
                }

                consent = nextConsent;
                writeNativeDismissalCookie();
                revokeOptionalCookies(consent.preferences);
                Configuration.recentlyViewedItems.useCookie = consent.preferences.personalization &&
                    originalTracking.recentlyViewed !== false;
                if (consent.preferences.analytics) {
                    startAnalytics(true);
                    if (mainAnalytics) {
                        MainAnalytics.replayCurrentPage();
                    }
                }
                if (!consent.preferences.analytics) {
                    MainAnalytics.setConsent(false);
                }
                restoreFocus(this);
                this.remove();
                if (activeView === this) {
                    activeView = null;
                }
                if (previousPreferences.analytics === true && consent.preferences.analytics === false) {
                    window.location.reload();
                }
            },

            getContext: function getCookieConsentContext() {
                var preferences = consent && consent.preferences || {};

                return {
                    manageMode: this.manageMode,
                    storageError: this.storageError,
                    analyticsSelected: preferences.analytics === true,
                    personalizationSelected: preferences.personalization === true
                };
            }
        });

        function connectView(banner) {
            if (!activeView) {
                activeView = new CookieConsentView();
            }
            if (activeView.el !== banner) {
                activeView.setElement(banner);
            }
            activeView.$el.attr('data-nms-cookie-ready', 'true');
            return activeView;
        }

        function removeBanner(banner) {
            var view = activeView;

            if (view) {
                view.remove();
                activeView = null;
            }
            if (banner && banner.parentNode) {
                banner.parentNode.removeChild(banner);
            }
        }

        function handleDocumentAction(event) {
            var actionButton = event.target && event.target.closest &&
                event.target.closest('#nms-cookie-consent [data-action]');
            var banner;
            var view;
            var action;

            if (!actionButton) {
                return;
            }

            banner = actionButton.closest('#nms-cookie-consent');
            if (!banner) {
                return;
            }

            view = connectView(banner);
            event.preventDefault();
            event.stopImmediatePropagation();
            action = actionButton.getAttribute('data-action');

            if (action === 'accept-necessary') {
                view.acceptNecessary(event);
            } else if (action === 'accept-all') {
                view.acceptAll(event);
            } else if (action === 'manage') {
                view.showPreferences(event);
            } else if (action === 'cancel-manage') {
                view.hidePreferences(event);
            } else if (action === 'save') {
                view.savePreferences(event);
            }
        }

        function installDocumentHandler() {
            if (!documentHandlerInstalled) {
                document.addEventListener('click', handleDocumentAction, true);
                documentHandlerInstalled = true;
            }
        }

        function appendCookieBanner(view) {
            var body = document.body;
            var anchor;
            var index;

            // Page Generator serializes this position into the public HTML.
            // Keep the banner ahead of blocking startup scripts so its fixed
            // content can paint with the page, while the existing control gate
            // still waits for the native application before enabling actions.
            if (typeof nsglobal !== 'undefined' && body && body.children) {
                for (index = 0; index < body.children.length; index += 1) {
                    if (String(body.children[index].tagName).toLowerCase() === 'script') {
                        anchor = body.children[index];
                        break;
                    }
                }
            }

            if (anchor) {
                body.insertBefore(view.el, anchor);
            } else {
                view.$el.appendTo(body);
            }
        }

        function mountCookieConsent() {
            var banners;
            var existingBanner;

            installDocumentHandler();
            removeNativeBanners();
            consent = readConsent();
            banners = document.querySelectorAll('[id="nms-cookie-consent"]');
            existingBanner = banners.length ? banners[0] : null;

            _.each(Array.prototype.slice.call(banners, 1), function removeDuplicateBanner(banner) {
                if (banner.parentNode) {
                    banner.parentNode.removeChild(banner);
                }
            });

            if (consent) {
                removeBanner(existingBanner);
                return;
            }

            if (existingBanner) {
                connectView(existingBanner);
            } else {
                activeView = new CookieConsentView();
                activeView.render();
                appendCookieBanner(activeView);
            }
        }

        consent = readConsent();
        applyStartupConsent(consent);
        if (consent) {
            writeNativeDismissalCookie();
        }
        revokeOptionalCookies(consent && consent.preferences || {});
        if (consent && consent.preferences.analytics === true) {
            startAnalytics();
        }

        return {
            mountToApp: function mountToApp(container) {
                var layout = container && container.getComponent && container.getComponent('Layout');

                mountCookieConsent();
                if (layout && !lifecycleBound) {
                    layout.on('afterShowContent', mountCookieConsent);
                    lifecycleBound = true;
                }
            },
            showPreferences: function showPreferences() {
                mountCookieConsent();
                if (activeView) {
                    activeView.showPreferences({preventDefault: function preventDefault() {}});
                }
            }
        };
    }
);
